import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Typography } from "@mui/material";
import { Button, Tag } from "pepsico-ds";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchHandler } from "../../../../../utils/functions";
import SelectableTable from "../../../../common/selectableTable/SelectableTable";
import ConditionFilters from "../conditionListing/ConditionFilters";
import "./retailerOutcome.scss";

const FILTERS = [
  { path: "retailerName", label: "Retailer Name" },
  { path: "storeName", label: "Store Name" },
  { path: "storeGroup", label: "Store Group" },
];

const HEADERS = [
  { columnKey: "retailerName", header: "Retailer Name" },
  { columnKey: "storeNames", header: "Store Name" },
  { columnKey: "storeGroups", header: "Store Group" },
];

const getOptions = (values) =>
  [...new Set(values.filter(Boolean))]
    .map(String)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({ id: value, displayText: value }));

const buildFilterConfig = (retailers) => {
  const valuesByFilter = {
    retailerName: retailers.map((retailer) => retailer.retailerName),
    storeName: retailers.flatMap((retailer) => retailer.storeNames),
    storeGroup: retailers.flatMap((retailer) => retailer.storeGroups),
  };
  const jsonSchema = {
    type: "object",
    properties: Object.fromEntries(
      FILTERS.map(({ path, label }) => [
        path,
        { type: "array", items: { type: "string" }, title: label },
      ])
    ),
  };
  const uiSchema = {
    type: "HorizontalLayout",
    elements: FILTERS.map(({ path, label }) => ({
      type: "Control",
      scope: "#/properties/" + path,
      component: "staticDropdownSelect",
      placeholder: label,
      options: {
        childList: getOptions(valuesByFilter[path]),
        selection: "multiple",
      },
    })),
  };

  return {
    withoutDebounce: { jsonSchema, uiSchema },
    allFilters: { jsonSchema, uiSchema },
  };
};

const renderTags = (values) => {
  if (!values.length) return "-";
  return (
    <span className="retailer-selector__tags">
      {values.slice(0, 2).map((value) => (
        <Tag
          key={value}
          text={value}
          color="Blue-500"
          isCopyable={false}
          isRemovable={false}
          size="small"
        />
      ))}
      {values.length > 2 && (
        <Tag
          text={`+${values.length - 2}`}
          color="Gray-300"
          isCopyable={false}
          isRemovable={false}
          size="small"
        />
      )}
    </span>
  );
};

const hasValue = (value) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && String(value).trim() !== "";

const getRetailerFilterValues = (retailer, path) => {
  if (path === "retailerName") return [retailer.retailerName];
  if (path === "storeName") return retailer.storeNames;
  if (path === "storeGroup") return retailer.storeGroups;
  return [];
};

const matchesFilters = (retailer, filters) =>
  Object.entries(filters).every(([path, selectedValue]) => {
    if (!hasValue(selectedValue)) return true;
    const selectedValues = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    const retailerValues = getRetailerFilterValues(retailer, path).map(String);
    return selectedValues
      .map(String)
      .some((value) => retailerValues.includes(value));
  });

const toRow = (retailer) => ({
  id: retailer.id,
  _searchText: [
    retailer.retailerName,
    ...retailer.storeNames,
    ...retailer.storeGroups,
  ].join(" "),
  retailerName: retailer.retailerName,
  storeNames: renderTags(retailer.storeNames),
  storeGroups: renderTags(retailer.storeGroups),
});

export default function RetailerSelector({
  retailers,
  initialSelection,
  onConfirm,
  onCancel,
  onBack,
}) {
  const { t } = useTranslation("RetailerOutcome");
  const [selectedRetailerIds, setSelectedRetailerIds] = useState(
    Array.isArray(initialSelection) ? initialSelection : []
  );
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const filterConfig = useMemo(() => buildFilterConfig(retailers), [retailers]);
  const rows = useMemo(
    () =>
      searchHandler(
        searchTerm,
        ["_searchText"],
        retailers.filter((retailer) => matchesFilters(retailer, filters)).map(toRow)
      ),
    [filters, retailers, searchTerm]
  );

  const handleConfirm = () => {
    if (selectedRetailerIds.length) onConfirm(selectedRetailerIds);
  };

  return (
    <div className="retailer-selector" data-testid="retailer-selector">
      <div className="retailer-selector__header">
        <Typography variant="h6" component="h2">
          {t("AddRetailersTitle")}
        </Typography>
        <IconButton aria-label="Close" onClick={onCancel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <ConditionFilters
        filterConfig={filterConfig}
        filterData={filters}
        onFilterChange={setFilters}
        onClear={() => setFilters({})}
        onSearch={setSearchTerm}
        clearButtonText={t("ResetFilters")}
      />

      <div className="retailer-selector__table">
        <SelectableTable
          rows={rows}
          headerData={HEADERS}
          selectedIds={selectedRetailerIds}
          onSelectionChange={setSelectedRetailerIds}
          className="campaign-retailer-listing"
          noDataLabel={t("NoRetailers")}
          rowsPerPage={10}
          highlightSelection
        />
      </div>

      <div className="retailer-selector__actions">
        <Button
          variant="tertiary"
          text={onBack ? t("Back") : t("Cancel")}
          size="medium"
          onClick={onBack || onCancel}
        />
        <Button
          variant="primary"
          text={t("AddCondition")}
          size="medium"
          onClick={handleConfirm}
          disabled={!selectedRetailerIds.length}
        />
      </div>
    </div>
  );
}

RetailerSelector.propTypes = {
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      retailerName: PropTypes.string.isRequired,
      storeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
      storeGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  initialSelection: PropTypes.arrayOf(PropTypes.string),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

RetailerSelector.defaultProps = {
  initialSelection: [],
  onBack: null,
};
